import { Call, Device } from "@twilio/voice-sdk";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useRef, useState } from "react";

type CallWithLocalStream = Call & {
  getLocalStream?: () => MediaStream | null;
};

function getLocalStreamFromCall(call: Call | null) {
  return (call as CallWithLocalStream | null)?.getLocalStream?.();
}

export const useVoIP = (onCallDisconnected?: () => void) => {
  const deviceRef = useRef<Device | null>(null);
  const currentCallRef = useRef<Call | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialize = async (_identity: string, token: string) => {
    if (!token) {
      throw new Error(
        "Access token is required for VoIP initialization",
      );
    }

    try {
      setError(null);
      deviceRef.current = new Device(token, {
        logLevel: 2,
        allowIncomingWhileBusy: true,
      });

      // Set up event listeners
      deviceRef.current.on("error", (err: { message?: string }) => {
        console.error("VoIP device error:", err);
        setError(err.message || "VoIP device error");
        mixpanel.track("VoIP Error", {
          action: "device_error",
          error: err.message || "VoIP device error",
        });
      });

      await deviceRef.current.register();
      setIsInitialized(true);

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to initialize VoIP device";
      console.error("Failed to initialize VoIP device:", err);
      setError(errorMessage);
      mixpanel.track("VoIP Error", {
        action: "initialize",
        error: errorMessage,
      });
      throw err;
    }
  };

  const joinConference = async (conferenceName: string) => {
    if (!deviceRef.current) {
      throw new Error("VoIP device not initialized");
    }

    try {
      setError(null);
      const call = await deviceRef.current.connect({
        params: {
          To: conferenceName,
        },
      });

      currentCallRef.current = call;
      setIsConnected(true);

      call.on("disconnect", () => {
        currentCallRef.current = null;
        setIsConnected(false);

        if (onCallDisconnected) {
          onCallDisconnected();
        }
      });

      return call;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to join conference";
      console.error("Failed to join conference via VoIP:", err);
      setError(errorMessage);
      mixpanel.track("VoIP Error", {
        action: "join_conference",
        conferenceName,
        error: errorMessage,
      });
      throw err;
    }
  };

  // Keep teardown callbacks stable so effect cleanup doesn't run on rerenders and drop active calls.
  const disconnect = useCallback(() => {
    if (currentCallRef.current) {
      const localStream = getLocalStreamFromCall(
        currentCallRef.current,
      );
      if (localStream) {
        localStream
          .getTracks()
          .forEach((track: MediaStreamTrack) => track.stop());
      }
      currentCallRef.current.disconnect();
      currentCallRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const destroy = useCallback(() => {
    disconnect();

    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }

    setIsInitialized(false);
    setIsConnected(false);
    setError(null);
  }, [disconnect]);

  const getCurrentCall = () => {
    return currentCallRef.current;
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (currentCallRef.current) {
        const localStream = getLocalStreamFromCall(
          currentCallRef.current,
        );
        if (localStream) {
          localStream
            .getTracks()
            .forEach((track: MediaStreamTrack) => track.stop());
        }
        currentCallRef.current.disconnect();
        currentCallRef.current = null;
      }

      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      setIsInitialized(false);
      setIsConnected(false);
      setError(null);
    };
  }, []);

  return {
    initialize,
    joinConference,
    disconnect,
    destroy,
    getCurrentCall,
    isInitialized,
    isConnected,
    error,
  };
};
